/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import {
  useEffect, useRef, useState, useCallback,
} from 'react';
import dayjs from 'dayjs';
import { useCheckoutStore } from '../../Hooks/Zustand/useCheckoutStore';
import { useQueryParamsStore } from '../../Hooks/Zustand/useQueryParamsStore';
import checkoutFormInitialValues from './FormModel/FormInitialValues';
import { useMutation } from '../../Hooks/API/useMutation';
import { isFormLoading } from '../../Signals/formStateSignal';
import { useCheckoutFormValidatorGenerator } from './FormModel/useCheckoutFormValidatorGenerator';
import DatalayerService from '../../Services/Tracking/DatalayerService';
import FacebookTrackingService from '../../Services/Tracking/FacebookTrackingService';
import { useDataAttributesStore } from '../../Hooks/Zustand/useDataAttributeStore';

/**
 * Custom Checkout hook
 * @param props
 */
export function useCheckout(props) {
  const { stepperLength } = props;
  const {
    getActiveStep,
    getLastValidStep,
    setActiveStep: setActiveStepInStore,
    setLastValidStep: setLastActiveStepInStore,
    getFormData,
    setFormData,
    getFormDataValue,
  } = useCheckoutStore();

  const {
    getQueryParameters,
    getQueryParameter,
  } = useQueryParamsStore();
  const {
    getDataAttributes,
    getDataAttribute,
  } = useDataAttributesStore();
  const [checkoutIsInitialized, setCheckoutIsInitialized] = useState(false);
  const checkoutFormDataIsInitialized = useRef(false);
  const [initialFormValues, setInitialFormValues] = useState(checkoutFormInitialValues);
  const lastValidStepInStore = getLastValidStep();
  const mutation = useMutation('createContract');

  /**
     * Checks if the studio data stored in the checkout store is correct.
     *
     * This function verifies whether the studio ID provided either as a data attribute
     * or as a query parameter matches the value stored in the form data.
     *
     * @returns {boolean} - Returns `true` if the studio data is incorrect and the IDs do not match.
     *                      Returns `false` if the studio data is correct and the IDs match.
     */
  const checkoutStoreContainsCorrectStudioData = useCallback(() => {
    const dataAttributeStudioId = getDataAttribute('studioId');
    const queryParameterStudioId = getQueryParameter('studioId');
    if (getDataAttribute('studioId') || getQueryParameter('studioId')) {
      return !((dataAttributeStudioId && (parseInt(dataAttributeStudioId, 10) !== getFormDataValue('studioId'))) || (queryParameterStudioId && (parseInt(queryParameterStudioId, 10) !== getFormDataValue('studioId'))));
    }
  }, []);

  /**
     * Initialize form context data
     */
  useEffect(() => {
    if (!checkoutFormDataIsInitialized.current) {
      setCheckoutIsInitialized(true);
      if (!checkoutStoreContainsCorrectStudioData()) {
        setFormData({});
      }
      setInitialFormValues({ ...initialFormValues, ...getFormData() });
      checkoutFormDataIsInitialized.current = true;
    }
  }, [getDataAttribute, getFormData, getFormDataValue, getQueryParameter, checkoutStoreContainsCorrectStudioData, initialFormValues, setActiveStepInStore, setFormData, setLastActiveStepInStore]);

  /**
     * Get the active form step
     * @returns {number|*}
     */
  const getActiveFormStep = () => {
    if (getActiveStep() && checkoutStoreContainsCorrectStudioData()) {
      return getActiveStep();
    }
    if (getQueryParameters().studioId) {
      setActiveStepInStore(1);
      return 1;
    }
    if (getDataAttributes().studioId) {
      setActiveStepInStore(1);
      return 1;
    }

    setActiveStepInStore(0);
    return 0;
  };

  /**
     * Get the last valid form step
     * @returns {number|*}
     */
  const getLastValidFormStep = () => {
    if (getLastValidStep() && checkoutStoreContainsCorrectStudioData()) {
      return getLastValidStep();
    }
    if (getQueryParameters().studioId) {
      setLastActiveStepInStore(1);
      return 1;
    }
    setLastActiveStepInStore(0);
    return 0;
  };

  /** Stepper last valid step */
  const [lastValidStep, setLastValidStep] = useState(getLastValidFormStep);
  /** Stepper active step */
  const [activeStep, setActiveStep] = useState(getActiveFormStep);
  /** Form Validation Schema */
  const { generateSchema } = useCheckoutFormValidatorGenerator();
  const [validationSchema, setValidationSchema] = useState(null);

  /**
     * Update validation schema when active step changes
     */
  useEffect(() => {
    setValidationSchema(generateSchema(getFormData())[activeStep]);
  }, [activeStep, generateSchema, getFormData]);

  /**
     * Whenever the last valid step in store updates, we need to set in the global state
     */
  useEffect(() => {
    if (lastValidStepInStore !== null) {
      setLastValidStep(lastValidStepInStore);
    }
  }, [lastValidStepInStore]);

  /**
     * Successfully created contract
     */
  const onSuccess = (response, requestArguments) => {
    // If the contract were successfully created, push den information to the datalayer and facebook
    DatalayerService.pushPurchaseToDatalayer(response, requestArguments, getFormData());
    FacebookTrackingService.pushPurchaseToFacebook();
    setLastValidStep(activeStep + 1);
    setActiveStep(activeStep + 1);
    setFormData({});
    setActiveStepInStore(null);
    setLastActiveStepInStore(null);
    isFormLoading.value = false;
  };

  /**
     * Error while contract creation
     */
  const onError = () => {
    isFormLoading.value = false;
  };

  /**
     * Get bankAccount data
     * @param values
     */
  const getBankAccount = (values) => {
    if (values.paymentChoice === 'DIRECT_DEBIT') {
      return {
        bankAccount: {
          iban: values.iban,
          accountHolder: values.accountOwner,
        },
      };
    }
    return {};
  };

  /**
     * Get creditCard data
     * @param values
     */
  const getCreditCard = (values) => {
    if (values.paymentChoice === 'CREDIT_CARD') {
      return {
        creditCard: {
          tokenizationReference: values.tokenizationReference,
        },
      };
    }
    return {};
  };

  /**
     * Return preview parameters
     */
  const getContractData = (values) => ({
    studioId: values.studioId,
    voucherCode: values.voucherCode,
    contract: {
      rateBundleTermId: values.rateBundleTerm.id,
      startDate: values.rateBundleTerm.defaultContractStartDate,
      preuseDate: values.rateBundleTerm.contractStartDateOfUse,
    },
    customer: {
      gender: values.gender,
      firstname: values.firstName,
      lastname: values.lastName,
      referralCode: values.referralCode,
      dateOfBirth: dayjs(values.dateOfBirth)
        .format('YYYY-MM-DD'),
      email: values.email,
      street: values.street,
      houseNumber: values.houseNumber,
      city: values.city,
      zipCode: values.postCode,
      countryCode: values.countryCode,
      paymentChoice: values.paymentChoice,
      ...getBankAccount(values),
      ...getCreditCard(values),
    },
  });

  /**
     * Submit form data
     * @param values
     */
  function submitForm(values) {
    isFormLoading.value = true;
    mutation.mutate(getContractData(values), {
      onSuccess,
      onError,
    });
  }

  /**
     * Handle back
     */
  function handleBack() {
    setActiveStepInStore(activeStep - 1);
    setActiveStep(activeStep - 1);
  }

  /**
     * Handle submit
     * @param values
     * @param actions
     */
  function handleSubmit(values, actions) {
    if (activeStep === stepperLength - 1) {
      submitForm(values, actions);
    } else {
      setFormData({ ...getFormData(), ...values });
      setActiveStepInStore(activeStep + 1);
      setLastActiveStepInStore(activeStep + 1);
      setLastValidStep(activeStep + 1);
      setActiveStep(activeStep + 1);

      actions.setTouched({});
      actions.setSubmitting(false);
    }
  }

  /**
     * allow only for valid steps
     * @param stepIndex
     */
  function handleStepperIconClick(stepIndex) {
    if (stepIndex <= lastValidStep) {
      setActiveStep(stepIndex);
    }
  }

  return {
    activeStep,
    lastValidStep,
    setActiveStep,
    setLastValidStep,
    validationSchema,
    initialFormValues,
    setValidationSchema,
    checkoutIsInitialized,
    handleBack,
    handleSubmit,
    handleStepperIconClick,
  };
}
